import { useState, useEffect } from "react";
import { Activity, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { getApiUrl } from "@/config/api";
import { authenticatedFetch } from "@/utils/apiUtils";
import { DataHealthResponse } from "@/types";

const STATUS_CONFIG = {
  green: { icon: CheckCircle2, color: "text-green-500", bg: "bg-green-500/10", label: "Ready" },
  yellow: { icon: AlertTriangle, color: "text-yellow-500", bg: "bg-yellow-500/10", label: "Approaching" },
  red: { icon: XCircle, color: "text-red-500", bg: "bg-red-500/10", label: "Not Ready" },
};

export default function RetrainReadinessCard() {
  const [data, setData] = useState<DataHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const run = async () => {
      const result = await authenticatedFetch<DataHealthResponse>(
        getApiUrl("/api/data-health/"),
        { method: "GET" },
        "fetching retrain readiness"
      );
      if (result.success && result.data) {
        setData(result.data);
      }
      setLoading(false);
    };
    run();
  }, []);

  if (loading) return <div className="cgm-card p-4 cgm-text-secondary text-sm">Loading readiness...</div>;
  if (!data?.retrain_readiness) return null;

  const r = data.retrain_readiness;
  const config = STATUS_CONFIG[r.status];
  const StatusIcon = config.icon;

  return (
    <div className="cgm-card p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Activity className="w-4 h-4 cgm-text-secondary" />
        <h3 className="text-sm font-semibold cgm-text-primary">Retrain Readiness</h3>
      </div>

      <div className={`flex items-center gap-3 p-3 rounded-lg ${config.bg}`}>
        <StatusIcon className={`w-5 h-5 ${config.color}`} />
        <span className={`text-sm font-bold ${config.color}`}>{config.label}</span>
      </div>

      <div className="space-y-2 text-xs">
        <div className="flex justify-between">
          <span className="cgm-text-secondary">Teams with data</span>
          <span className="cgm-text-primary font-medium">{r.team_count} / {r.teams_needed}</span>
        </div>
        <div className="flex justify-between">
          <span className="cgm-text-secondary">Injury labels</span>
          <span className="cgm-text-primary font-medium">{r.injury_labels} / {r.labels_needed}</span>
        </div>
      </div>

      <div className="pt-3 space-y-1.5" style={{ borderTop: '1px solid var(--card-border)' }}>
        <span className="text-[10px] cgm-text-secondary uppercase tracking-wider">Per-team breakdown</span>
        {data.teams.map((team) => (
          <div key={team.id} className="flex justify-between text-xs">
            <span className="cgm-text-primary">{team.name}</span>
            <span className="cgm-text-secondary">
              {team.athlete_count} athletes, {team.injury_records} injuries
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
